/**
 * Age-band derivation (M12, D3 / §9 privacy).
 *
 * {@link AgeBand} is the ONLY age representation that ever reaches the generation
 * agent or goes on the wire — raw ages and birth years are never transmitted.
 * Guests self-report a band directly at join; a registered player's band is
 * derived here from their profile `birthYear` at game start, server-side.
 *
 * Band boundaries (inclusive lower bounds), chosen to match the age-appropriate
 * content tiers the generator targets:
 *   - `KID`   : age  < 13
 *   - `TEEN`  : 13 ≤ age < 18
 *   - `ADULT` : age ≥ 18
 *
 * Pure: no clock. The caller passes the current year (from its injectable clock)
 * so this stays deterministic and testable.
 */

import type { AgeBand } from './domain';

/** Lower age bound (inclusive) for each non-KID band. */
const TEEN_MIN_AGE = 13;
const ADULT_MIN_AGE = 18;

/**
 * Minimum age to create an account (FR-14.1, COPPA neutral age gate). Equal to the
 * TEEN band lower bound (13) on purpose — the registration gate and the age-band
 * derivation share ONE age boundary, so there is no second, drifting definition of
 * "under 13". A registrant younger than this is blocked before any account/PII is
 * created (#41 D-41.2 / D-7).
 */
export const MIN_REGISTRATION_AGE = TEEN_MIN_AGE;

/**
 * A neutrally-collected date of birth (FR-14.1). `month` is 1–12 and `day` is 1–31.
 * Collected in full at registration so the age gate is precise near a birthday; only
 * the {@link DateOfBirth.year} is ever persisted (as the profile `birthYear`) — the
 * raw month/day are used for the gate and then discarded (#41 D-6).
 */
export interface DateOfBirth {
  year: number;
  month: number;
  day: number;
}

/** The current calendar date, injected so the age helpers stay clock-free + testable. */
export interface CalendarDate {
  year: number;
  month: number;
  day: number;
}

/**
 * True if `dob` is a real calendar date (correct days-per-month incl. leap years) with a
 * plausible year in `[1900, now.year]` and not in the future. Pure: the caller passes
 * the current date so this never reads the clock. `Date.UTC` is used only for
 * deterministic calendar arithmetic on the explicit inputs — not to read "now".
 */
export function isValidDateOfBirth(dob: DateOfBirth, now: CalendarDate): boolean {
  const { year, month, day } = dob;
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    return false;
  }
  if (year < 1900 || year > now.year) return false;
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;
  // Reject impossible days (e.g. Feb 30, Apr 31) via a round-trip through a UTC date.
  const asDate = new Date(Date.UTC(year, month - 1, day));
  if (
    asDate.getUTCFullYear() !== year ||
    asDate.getUTCMonth() !== month - 1 ||
    asDate.getUTCDate() !== day
  ) {
    return false;
  }
  // A future date of birth is never valid (guards a same-year future month/day).
  if (
    year === now.year &&
    (month > now.month || (month === now.month && day > now.day))
  ) {
    return false;
  }
  return true;
}

/**
 * Whole-year age from a date of birth and the current date. Precise near birthdays:
 * someone born on 2013-08-26 is 12 on 2026-08-25 and 13 on 2026-08-26. Pure (clock
 * injected). Assumes `dob` is already valid (see {@link isValidDateOfBirth}).
 */
export function ageFromDateOfBirth(dob: DateOfBirth, now: CalendarDate): number {
  let age = now.year - dob.year;
  if (now.month < dob.month || (now.month === dob.month && now.day < dob.day)) {
    age -= 1;
  }
  return age;
}

/**
 * True if a registrant with date of birth `dob` is below {@link MIN_REGISTRATION_AGE}
 * as of `now` — the server-authoritative COPPA gate (FR-14.1). A caller should first
 * confirm {@link isValidDateOfBirth}; an invalid DOB is treated as under-age (fail
 * closed) so a malformed value can never slip past the gate.
 */
export function isUnderMinimumAge(dob: DateOfBirth, now: CalendarDate): boolean {
  if (!isValidDateOfBirth(dob, now)) return true;
  return ageFromDateOfBirth(dob, now) < MIN_REGISTRATION_AGE;
}

/**
 * Derive an {@link AgeBand} from a birth year and the current year. Returns
 * `undefined` when `birthYear` is absent or implausible (future year, or an age
 * beyond a sane human range) so a bad value degrades to "no band" rather than a
 * wrong band — the generator then treats that player as unconstrained.
 */
export function ageBandFromBirthYear(
  birthYear: number | undefined,
  currentYear: number,
): AgeBand | undefined {
  if (birthYear === undefined || !Number.isInteger(birthYear)) return undefined;
  const age = currentYear - birthYear;
  if (age < 0 || age > 120) return undefined;
  if (age < TEEN_MIN_AGE) return 'KID';
  if (age < ADULT_MIN_AGE) return 'TEEN';
  return 'ADULT';
}

/** True if `value` is a valid {@link AgeBand} (wire-input validation). */
export function isAgeBand(value: unknown): value is AgeBand {
  return value === 'KID' || value === 'TEEN' || value === 'ADULT';
}
